import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import { sendTextMessage, useTextMessages, viewTextMessagesForApplication } from '../../Data/TextMessageData';
import Divider from '@mui/material/Divider';
import { ApplicationDocumentViewer } from '../ApplicationDocumentViewer';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationDispatch, SET_LOADING, SET_SERVER_MESSAGE } from '../../store/application/types';
import { AppState } from '../../store/rootReducer';

export interface TextMessageChatProps {
  applicationId?: string;
  rightSideBarWidth: number;
}

export const TextMessageChat: React.FC<TextMessageChatProps> = props => {
  const dispatch = useDispatch<ApplicationDispatch>();
  const [isExpanded, setIsExpanded] = useState(false);
  const [toNumber, setToNumber] = useState('');
  const [textMessage, setTextMessage] = useState('');
  const { resolvedData, isFetching } = useTextMessages({ applicationId: props.applicationId });
  const textMessages = resolvedData?.textMessages;
  const unviewedTextMessages = textMessages?.filter(x => !x.hasBeenViewed);
  const appCustomers = useSelector((state: AppState) => state.applications.editingApp?.appCustomers);
  const firstPreferredCustomerPhone = appCustomers
    ?.map(ac => ac.customer?.customerPhone?.find(cp => cp.isPreferred && !cp.doNotText))
    ?.find(cp => cp && cp.isPreferred && !cp.doNotText);

  useEffect(() => {
    if (firstPreferredCustomerPhone?.phoneNumber) {
      setToNumber(firstPreferredCustomerPhone.phoneNumber);
    } else {
      setToNumber('');
    }
  }, [appCustomers, firstPreferredCustomerPhone]);

  return (
  <div  
    style={{
      display: 'flex',
      position: 'fixed',
      bottom: 0,
      right: `${props.rightSideBarWidth ? (props.rightSideBarWidth + 25) : 16}px`,
      width: 400,
      maxWidth: '80vw',
      // minHeight: 300,
      maxHeight: '80vh',
      zIndex: 1201,
      backgroundColor: 'white',
      borderRadius: '5px 5px 0px 0px',
      boxShadow: '0px 0px 3px 0px black',
      flexDirection: 'column',
    }}
  >
    <div
      style={{
        display: 'flex',
        padding: '0px 12px',
        backgroundColor: '#eee',
        borderRadius: '5px 5px 0px 0px',
        alignItems: 'center',
        cursor: 'pointer',
      }}
      onClick={async () => {
        setIsExpanded(!isExpanded);
        
        if (!isExpanded 
          && props.applicationId
          && unviewedTextMessages?.length) {
          await viewTextMessagesForApplication(props.applicationId, dispatch);
        }
      }}
    >
      <Typography variant="h6"        
        style={{
          flex: '1 1 auto',
        }}
      >
        Text Messages
        {!!unviewedTextMessages?.length && ` (${unviewedTextMessages?.length})`}
      </Typography>
      <IconButton
        
      >
        {isExpanded 
          ? <ExpandMoreIcon />
          : <ExpandLessIcon />
        }        
      </IconButton>
    </div>
    {isExpanded &&
    <>
    <div
      style={{
        flex: '1 1 auto',
        display: 'flex',
        flexDirection: 'column-reverse',
        overflowY: 'auto',
      }}
    >
      {isFetching && !textMessages?.length &&
      <Typography variant="body2"
        style={{
          alignSelf: 'center',
          justifySelf: 'center',
          height: '50%',
        }}
      >
        Loading...
      </Typography>
      }
      {!isFetching && !textMessages?.length &&
      <Typography variant="body2"
        style={{
          alignSelf: 'center',
          justifySelf: 'center',
          height: '50%',
        }}
      >
        No text messages yet
      </Typography>
      }
      {textMessages?.map((m, i) => 
        <div key={i}
          style={{
            display: 'flex',
            flexDirection: m.fromLos ? 'row-reverse' : 'row',
            padding: 12,
          }}
        >
          <div
            style={{
              borderRadius: m.fromLos
                ? '5px 0px 5px 5px'
                : '0px 5px 5px 5px',
              boxShadow: 'black 0px 0px 2px 0px',
              maxWidth: '80%',
              backgroundColor: m.fromLos ? '#e6fde5' : 'aliceblue',
              padding: 10,
            }}
          >
            <Typography variant="body2">
              {m.createdDateFormatted}
            </Typography>
            
            <Typography variant="body2">
              {m.phoneNumberFrom}
            </Typography>
            
            <Divider variant="fullWidth" 
              style={{
                margin: '6px 0px',
              }}
            />

            <Typography variant="body1">
              {m.message}
            </Typography>

            {m.applicationDocuments?.map(ad => 
            <ApplicationDocumentViewer 
              key={ad.documentId}
              selectedDocument={ad}
            />
            )}
          </div>
        </div>
      )}
    </div>
    <div
      style={{
        padding: '6px 12px',
      }}
    >
        <TextField
          value={toNumber}
          onChange={(e) => {
            setToNumber(e.target.value);
          }}
          select
          variant="outlined"
          label="Phone Number"
          fullWidth
          size="small"
          style={{
            marginBottom: 4,
          }}
          SelectProps={{
            native: true,
          }}
        >
          {/* <option>&nbsp;</option> */}
          {appCustomers?.map(ac =>
            ac.customer?.customerPhone
            ?.filter(cp => !cp.doNotText)
            ?.map(cp => 
            <option value={cp.phoneNumber}>{cp.phoneNumber} ({ac.customer.firstName} {ac.customer.lastName}{cp.isPreferred ? ' - Preferred': ''})</option>
            )
          )}
        </TextField>
        <TextField
          value={textMessage}
          onChange={(e) => {
            setTextMessage(e.target.value);
          }}
          variant="outlined"
          placeholder="Send text message..."
          fullWidth
          multiline
          maxRows={4}
          size="small"
          InputProps={{
            endAdornment: (
            <IconButton color="primary"
              onClick={async () => {
                try {
                  dispatch({ type: SET_LOADING, payload: true });
                  if (!toNumber || !textMessage || !props.applicationId) {
                    dispatch({ 
                      type: SET_SERVER_MESSAGE, 
                      payload: {
                        message: `Please enter some text for the text message.`,
                        isError: false,
                      },
                    });
                  } else {
                    const response = await sendTextMessage(
                      toNumber,
                      textMessage,
                      props.applicationId,
                      dispatch
                    );
                    setTextMessage(''); // Clear out the text message box.
                  }
                } catch (error) {
                  dispatch({ 
                    type: SET_SERVER_MESSAGE, 
                    payload: {
                      message: error as string,
                      isError: true,
                    },
                  });
                } finally {
                  dispatch({ type: SET_LOADING, payload: false });
                }
              }}
            >
              <SendIcon />
            </IconButton>
            )
          }}
        />
    </div>
    </>
    }
  </div>
  );
}
